// src/pages/driverReports/CarSheetCompanyPLExtract.js
// Row-wise extract from already-built carSheets/{month}/sheets documents.
// It does NOT rebuild car sheets.

import React, { useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { db } from "../../firebase/firebase";

const BUCKETS = [
  "Comfort Fixed",
  "Comfort Salary",
  "Lolia Fixed",
  "Lolia Salary",
  "Other / Review",
];

const RIDE_HAILING = ["UBER", "CAREEM", "BOLT", "YANGO", "ZED"];
const DEFAULT_HEADS = {
  Expenses: [
    "MONTHLY FIXED COST",
    "SALIK",
    "PARKONIC",
    "RTA PARKING",
    "DARB",
    "PETROL",
    "WPS CHARGES",
    "TRAFFIC FINES",
    "ADDITIONAL DRIVER",
    "BALANCE BROUGHT FORWARD",
    "ADVANCE PAYMENT",
    "PERMIT RENEWAL",
    "MULKIA/INSURANCE RENEWAL",
    "OTHER EXPENSES",
    "MISC ADJUSTMENT",
    "PARKING, MAINTENANCE ETC",
    "DEWA",
    "TAX",
    "CASH RECEIVED / PAID TO DRIVER",
  ],
  Income: ["UBER", "BOLT", "CAREEM", "YANGO", "ZED", "POS", "OTHER ADJUSTMENT", "PAYMENT RECEIVED"],
  "Cash Trips": ["UBER CASH", "BOLT CASH", "CAREEM CASH", "YANGO CASH", "ZED CASH"],
};

function norm(value) {
  return String(value || "").trim();
}

function upper(value) {
  return norm(value).toUpperCase().replace(/\s+/g, " ");
}

function amount(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

function money(value) {
  return amount(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function makeMonthRange(startMonth, endMonth) {
  if (!startMonth || !endMonth) return [];
  const [sy, sm] = startMonth.split("-").map(Number);
  const [ey, em] = endMonth.split("-").map(Number);
  if (!sy || !sm || !ey || !em) return [];

  const start = new Date(sy, sm - 1, 1);
  const end = new Date(ey, em - 1, 1);
  if (start > end) return [];

  const out = [];
  const d = new Date(start);
  while (d <= end) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    d.setMonth(d.getMonth() + 1);
  }
  return out;
}

function toDate(value) {
  try {
    if (!value) return null;
    if (typeof value?.toDate === "function") return value.toDate();
    if (value instanceof Date) return value;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function getCompanyPart(company) {
  const c = upper(company);
  if (c.includes("LOLIA")) return "Lolia";
  if (c.includes("COMFORT")) return "Comfort";
  return "Other";
}

function getPlanPart(plan) {
  const p = upper(plan);
  if (p.includes("SALARY") || p.includes("COMMISSION")) return "Salary";
  if (p.includes("FIXED")) return "Fixed";
  return "Review";
}

function bucketFrom(company, plan) {
  const c = getCompanyPart(company);
  const p = getPlanPart(plan);
  if (c === "Comfort" && p === "Fixed") return "Comfort Fixed";
  if (c === "Comfort" && p === "Salary") return "Comfort Salary";
  if (c === "Lolia" && p === "Fixed") return "Lolia Fixed";
  if (c === "Lolia" && p === "Salary") return "Lolia Salary";
  return "Other / Review";
}

async function loadDriversMap() {
  const snap = await getDocs(collection(db, "drivers"));
  const map = {};
  snap.forEach((d) => {
    const x = d.data();
    map[d.id] = {
      id: d.id,
      company: x.company || x.assignedCarCompany || "",
      driverName: x.fullName || x.driverName || x.name || "",
    };
  });
  return map;
}

async function loadDriverPlanMap() {
  const snap = await getDocs(collection(db, "driverPlanHistory"));
  const map = {};
  snap.forEach((d) => {
    const x = d.data();
    if (!x.driverId) return;
    if (!map[x.driverId]) map[x.driverId] = [];
    map[x.driverId].push({
      driverId: x.driverId,
      planType: x.planType || "FIXED",
      startDate: toDate(x.startDate),
      endDate: toDate(x.endDate),
    });
  });
  Object.values(map).forEach((list) => {
    list.sort((a, b) => (toDate(a.startDate)?.getTime() || 0) - (toDate(b.startDate)?.getTime() || 0));
  });
  return map;
}

function getDriverPlan(driverId, date, planMap, fallbackPlan = "FIXED") {
  const list = planMap?.[driverId] || [];
  const ref = toDate(date) || new Date();
  const match = list
    .filter((p) => {
      const s = toDate(p.startDate);
      const e = toDate(p.endDate) || new Date(8640000000000000);
      return s && ref >= s && ref <= e;
    })
    .sort((a, b) => (toDate(b.startDate)?.getTime() || 0) - (toDate(a.startDate)?.getTime() || 0))[0];
  return match?.planType || fallbackPlan || "FIXED";
}

function rowLabel(row) {
  return upper(row?.label || row?.company || row?.platform || row?.id || "");
}

function rowMatches(row, selectedHead) {
  const target = upper(selectedHead);
  if (!target) return false;
  return rowLabel(row) === target;
}

function isPosLabel(label) {
  return upper(label).includes("POS");
}

function isRideOrDriverIncome(label) {
  const u = upper(label).replace(" CASH", "");
  return RIDE_HAILING.some((p) => u === p || u.startsWith(p + " ")) ||
    ["OTHER ADJUSTMENT", "PAYMENT RECEIVED"].includes(upper(label));
}

function getAllocationBucket({ section, row, sheet, driversMap, planMap }) {
  const label = rowLabel(row);

  // Expenses and POS are car based.
  if (section === "Expenses" || isPosLabel(label)) {
    return bucketFrom(sheet.company, sheet.planType);
  }

  // Ride-hailing/manual bank and cash trips are driver based when driver is known.
  if ((section === "Income" || section === "Cash Trips") && isRideOrDriverIncome(label)) {
    const driverId = row.driverId || row.driverID || "";
    const driver = driverId ? driversMap[driverId] : null;
    const company = driver?.company || row.driverCompany || sheet.driverCompany || sheet.company;
    const plan = driverId
      ? getDriverPlan(driverId, sheet.periodStart || `${sheet.month || ""}-01`, planMap, sheet.planType)
      : sheet.planType;
    return bucketFrom(company, plan);
  }

  return bucketFrom(sheet.company, sheet.planType);
}

function getRowsBySection(sheet, section) {
  if (section === "Expenses") return (sheet.expensesRows || []).filter((r) => !r.isSummary && upper(r.label) !== "TOTAL EXPENSES");
  if (section === "Cash Trips") return (sheet.cashRows || []).filter((r) => r.rowType === "data");
  return (sheet.incomeRows || []).filter((r) => r.rowType === "data");
}

function extractAvailableHeads(sheets, section) {
  const set = new Set(DEFAULT_HEADS[section] || []);
  sheets.forEach((sheet) => {
    getRowsBySection(sheet, section).forEach((r) => {
      const l = rowLabel(r);
      if (l) set.add(l);
    });
  });
  return Array.from(set).sort();
}

async function loadSavedSheets(months) {
  const all = [];
  for (const month of months) {
    const snap = await getDocs(collection(db, "carSheets", month, "sheets"));
    snap.forEach((d) => all.push({ id: d.id, month, ...d.data() }));
  }
  return all;
}

function buildRowExtract({ sheets, months, section, selectedHead, driversMap, planMap }) {
  const summaryMap = {};
  const detailRows = [];

  months.forEach((m) => {
    summaryMap[m] = { month: m, head: selectedHead, total: 0 };
    BUCKETS.forEach((b) => (summaryMap[m][b] = 0));
  });

  sheets.forEach((sheet) => {
    const month = sheet.month || "";
    if (!summaryMap[month]) return;

    getRowsBySection(sheet, section)
      .filter((r) => rowMatches(r, selectedHead))
      .forEach((r) => {
        const val = amount(r.total);
        if (val === 0) return;
        const bucket = getAllocationBucket({ section, row: r, sheet, driversMap, planMap });
        summaryMap[month][bucket] = amount(summaryMap[month][bucket]) + val;
        summaryMap[month].total = amount(summaryMap[month].total) + val;
        detailRows.push({
          month,
          bucket,
          section,
          head: selectedHead,
          carNumber: sheet.carNumber || "",
          company: sheet.company || "",
          planType: sheet.planType || "",
          periodLabel: sheet.periodLabel || "",
          driver: r.driver || r.driverName || "",
          driverId: r.driverId || "",
          amount: val,
          details: r.autoDetails || r.description || r.col1 || "",
        });
      });
  });

  return {
    summaryRows: Object.values(summaryMap),
    detailRows,
  };
}

async function exportRowExtract({ startMonth, endMonth, section, selectedHead, summaryRows, detailRows }) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "CLL Dashboard";
  wb.created = new Date();

  const summary = wb.addWorksheet("Month Summary");
  summary.columns = [
    { header: "Month", key: "month", width: 12 },
    { header: "Head", key: "head", width: 28 },
    ...BUCKETS.map((b) => ({ header: b, key: b, width: 18 })),
    { header: "Total", key: "total", width: 18 },
  ];
  summaryRows.forEach((r) => summary.addRow(r));

  const detail = wb.addWorksheet("Details");
  detail.columns = [
    { header: "Month", key: "month", width: 12 },
    { header: "Bucket", key: "bucket", width: 20 },
    { header: "Section", key: "section", width: 14 },
    { header: "Head", key: "head", width: 28 },
    { header: "Car Number", key: "carNumber", width: 18 },
    { header: "Company", key: "company", width: 36 },
    { header: "Plan", key: "planType", width: 14 },
    { header: "Period", key: "periodLabel", width: 24 },
    { header: "Driver", key: "driver", width: 35 },
    { header: "Driver ID", key: "driverId", width: 22 },
    { header: "Amount", key: "amount", width: 16 },
    { header: "Details", key: "details", width: 60 },
  ];
  detailRows.forEach((r) => detail.addRow(r));

  [summary, detail].forEach((ws) => {
    ws.getRow(1).font = { bold: true };
    ws.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE3F2FD" } };
    ws.eachRow((row, rowNumber) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin", color: { argb: "FFDDDDDD" } },
          left: { style: "thin", color: { argb: "FFDDDDDD" } },
          bottom: { style: "thin", color: { argb: "FFDDDDDD" } },
          right: { style: "thin", color: { argb: "FFDDDDDD" } },
        };
        cell.alignment = { vertical: "middle", wrapText: rowNumber === 1 ? true : false };
        if (typeof cell.value === "number") {
          cell.numFmt = '"AED" #,##0.00;[Red]-"AED" #,##0.00';
        }
      });
    });
  });

  const safeHead = upper(selectedHead).replace(/[^A-Z0-9]+/g, "_");
  const buffer = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `CarSheet_Row_Extract_${safeHead}_${startMonth}_to_${endMonth}.xlsx`
  );
}

export default function CarSheetCompanyPLExtract() {
  const [startMonth, setStartMonth] = useState("2026-01");
  const [endMonth, setEndMonth] = useState("2026-06");
  const [section, setSection] = useState("Expenses");
  const [selectedHead, setSelectedHead] = useState("SALIK");
  const [sheets, setSheets] = useState([]);
  const [driversMap, setDriversMap] = useState({});
  const [planMap, setPlanMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const months = useMemo(() => makeMonthRange(startMonth, endMonth), [startMonth, endMonth]);
  const availableHeads = useMemo(() => extractAvailableHeads(sheets, section), [sheets, section]);
  const { summaryRows, detailRows } = useMemo(
    () => buildRowExtract({ sheets, months, section, selectedHead, driversMap, planMap }),
    [sheets, months, section, selectedHead, driversMap, planMap]
  );

  async function handleLoad() {
    if (!months.length) {
      alert("Please select a valid month range.");
      return;
    }
    if (!selectedHead) {
      alert("Please select row/head.");
      return;
    }

    setLoading(true);
    setMessage("");
    try {
      const [sheetRows, driverRows, planRows] = await Promise.all([
        loadSavedSheets(months),
        loadDriversMap(),
        loadDriverPlanMap(),
      ]);
      setSheets(sheetRows);
      setDriversMap(driverRows);
      setPlanMap(planRows);
      setMessage(`Loaded ${sheetRows.length} saved car sheets from ${months.length} month(s). No rebuild was performed.`);
    } catch (e) {
      console.error(e);
      alert(e.message || "Failed to load saved car sheets.");
    } finally {
      setLoading(false);
    }
  }

  function handleSectionChange(next) {
    setSection(next);
    const heads = DEFAULT_HEADS[next] || [];
    setSelectedHead(heads[0] || "");
  }

  return (
    <div style={{ padding: 24, fontFamily: "Arial, sans-serif" }}>
      <h2 style={{ marginTop: 0 }}>Car Sheet Row Extract</h2>
      <p style={{ color: "#555", maxWidth: 1000 }}>
        Select any saved car-sheet row/head and month range. The report shows month-wise totals split by
        Comfort Fixed, Comfort Salary, Lolia Fixed, and Lolia Salary. It reads already-built car sheets only.
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap", marginBottom: 16 }}>
        <label>
          <div style={labelStyle}>From month</div>
          <input type="month" value={startMonth} onChange={(e) => setStartMonth(e.target.value)} style={inputStyle} />
        </label>
        <label>
          <div style={labelStyle}>To month</div>
          <input type="month" value={endMonth} onChange={(e) => setEndMonth(e.target.value)} style={inputStyle} />
        </label>
        <label>
          <div style={labelStyle}>Section</div>
          <select value={section} onChange={(e) => handleSectionChange(e.target.value)} style={inputStyle}>
            <option value="Expenses">Expenses</option>
            <option value="Income">Income</option>
            <option value="Cash Trips">Cash Trips</option>
          </select>
        </label>
        <label>
          <div style={labelStyle}>Row / Head</div>
          <input
            list="car-sheet-heads"
            value={selectedHead}
            onChange={(e) => setSelectedHead(e.target.value)}
            style={{ ...inputStyle, minWidth: 260 }}
            placeholder="SALIK / PETROL / UBER"
          />
          <datalist id="car-sheet-heads">
            {availableHeads.map((h) => <option key={h} value={h} />)}
          </datalist>
        </label>
        <button onClick={handleLoad} disabled={loading} style={primaryBtn}>
          {loading ? "Loading..." : "Generate Report"}
        </button>
        <button
          onClick={() => exportRowExtract({ startMonth, endMonth, section, selectedHead, summaryRows, detailRows })}
          disabled={loading || !summaryRows.length}
          style={greenBtn}
        >
          Export Excel
        </button>
      </div>

      {message && <div style={infoBox}>{message}</div>}

      <h3>Month-wise summary</h3>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={th}>Month</th>
            <th style={th}>Row / Head</th>
            {BUCKETS.map((b) => <th key={b} style={th}>{b}</th>)}
            <th style={th}>Total</th>
          </tr>
        </thead>
        <tbody>
          {!summaryRows.length ? (
            <tr><td style={td} colSpan={8}>No data loaded yet.</td></tr>
          ) : (
            summaryRows.map((r) => (
              <tr key={r.month}>
                <td style={tdStrong}>{r.month}</td>
                <td style={td}>{r.head}</td>
                {BUCKETS.map((b) => <td key={b} style={tdRight}>AED {money(r[b])}</td>)}
                <td style={tdRight}><b>AED {money(r.total)}</b></td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <h3>Detail rows</h3>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={th}>Month</th>
            <th style={th}>Bucket</th>
            <th style={th}>Car</th>
            <th style={th}>Period</th>
            <th style={th}>Driver</th>
            <th style={th}>Amount</th>
            <th style={th}>Details</th>
          </tr>
        </thead>
        <tbody>
          {!detailRows.length ? (
            <tr><td style={td} colSpan={7}>No matching rows.</td></tr>
          ) : (
            detailRows.map((r, idx) => (
              <tr key={`${r.month}-${r.carNumber}-${idx}`}>
                <td style={td}>{r.month}</td>
                <td style={td}>{r.bucket}</td>
                <td style={td}>{r.carNumber}</td>
                <td style={td}>{r.periodLabel}</td>
                <td style={td}>{r.driver || "-"}</td>
                <td style={tdRight}>AED {money(r.amount)}</td>
                <td style={td}>{r.details || "-"}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

const labelStyle = { fontSize: 13, fontWeight: 700, marginBottom: 4 };
const inputStyle = { padding: 8, border: "1px solid #ccc", borderRadius: 4 };
const primaryBtn = { padding: "9px 14px", background: "#0d47a1", color: "white", border: "none", borderRadius: 5, cursor: "pointer", fontWeight: 700 };
const greenBtn = { ...primaryBtn, background: "#1b7f37" };
const infoBox = { padding: 10, background: "#eef6ff", border: "1px solid #bcd7ff", borderRadius: 6, marginBottom: 16 };
const tableStyle = { width: "100%", borderCollapse: "collapse", marginTop: 10, marginBottom: 24, fontSize: 13 };
const th = { border: "1px solid #ddd", background: "#e3f2fd", padding: 8, textAlign: "left", whiteSpace: "nowrap" };
const td = { border: "1px solid #ddd", padding: 8 };
const tdStrong = { ...td, fontWeight: 700 };
const tdRight = { ...td, textAlign: "right", whiteSpace: "nowrap" };
